// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { mergeWithCss } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { Clickable, LazyImage } from "@insite/mobius/index";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface OwnProps {
    buttonLabel: string;
    title: string;
    titleLink?: LinkFieldValue;
    titleSizeVariant: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
    subtitle: string;
    textContent: string;
    subtitleUrl: LinkFieldValue;
    buttonLink: LinkFieldValue;
    buttonLinkSize: "small" | "large";
    image: string;
    imageCaption?: string;
    imagePosition?: string;
    extendedStyles?: InfoCardStyles;
    buttonAlignment: "left" | "right" | "center";
    openInNewTab?: boolean;
}

type Props = OwnProps;

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    imageClickableWrapper?: ClickablePresentationProps;
    image?: LazyImageProps;
    cardContentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    subtitle?: LinkPresentationProps;
    textContent?: TypographyPresentationProps;
    smallLink?: LinkPresentationProps;
    link?: LinkPresentationProps;
    buttonWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const InfoCard: React.FC<Props> = ({
    buttonLabel,
    title,
    titleSizeVariant,
    subtitle,
    subtitleUrl,
    textContent,
    buttonLink,
    buttonLinkSize = "small",
    image,
    imageCaption,
    imagePosition,
    extendedStyles,
    buttonAlignment,
    titleLink,
    openInNewTab,
}) => {
    const [styles] = React.useState(() => mergeWithCss(infoCardStyles, extendedStyles));
    const link = useGetLink(buttonLink);
    const subtitleLink = useGetLink(subtitleUrl);
    const { url } = useGetLink(titleLink ?? { type: "Page", value: "" });

    const styledProps = { imagePosition };

    return (
        <StyledWrapper {...styles.wrapper} {...styledProps}>
            <Clickable
                href={link.url}
                target={openInNewTab ? "_blank" : "_self"}
                aria-label={`${buttonLabel} ${link.title}`}
                {...styles.imageClickableWrapper}
                {...styledProps}
            >
                <LazyImage src={image} {...styles.image} altText={imageCaption || "Conklin Card Image"} />
            </Clickable>
            <StyledWrapper {...styles.cardContentWrapper}>
                <Link
                    href={subtitleLink.url || link.url}
                    target={openInNewTab ? "_blank" : "_self"}
                    {...styles.subtitle}
                    aria-label="subtitle"
                >
                    {subtitle}
                </Link>
                {url ? (
                    <Link
                        href={url}
                        target={openInNewTab ? "_blank" : "_self"}
                        {...styles.titleLink}
                        aria-label="title"
                    >
                        {title}
                    </Link>
                ) : (
                    <Typography {...styles.title} variant={titleSizeVariant}>
                        {title}
                    </Typography>
                )}
                <Typography {...styles.textContent}>{textContent}</Typography>
                {link.url && (
                    <StyledWrapper {...styles.buttonWrapper} {...{ buttonAlignment }}>
                        <Link
                            href={link.url}
                            target={openInNewTab ? "_blank" : "_self"}
                            {...(buttonLinkSize === "small" ? styles.smallLink : styles.link)}
                            aria-label={`${buttonLabel} ${link.title}`}
                        >
                            {buttonLabel || link.title || link.url}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default InfoCard;
